import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import useCapabilities, { PERMISSION } from "@/hooks/useCapabilities";
import { DASHBOARD_EVENTS_QUERY } from "../queries";
import { DASHBOARD_PAST_EVENT_LIMIT, DASHBOARD_SCHEDULE_MAX_PAGE_SIZE } from "../constants";
import { groupScheduleEventsByMonth, toDashboardPastEvent } from "../_utils/dashboardEventDisplay";
import { getDashboardScheduleDateRange } from "../_utils/dashboardScheduleDateRange";
import { isInitialQueryLoading } from "../_utils/isInitialQueryLoading";
import { useDashboardEventSectionIds } from "./useDashboardEventSectionIds";

const SCHEDULE_STATUS_VALUES = ["Active", "Pending", "Draft"] as const;

export default function useDashboardEvents() {
  const { hasPermission, loaded: capabilitiesLoaded } = useCapabilities();
  const canViewEvents = capabilitiesLoaded && hasPermission(PERMISSION.manageEvents);
  const {
    sectionIds,
    scopedSectionNames,
    loading: sectionIdsLoading,
    ready: sectionIdsReady
  } = useDashboardEventSectionIds();

  const sharedQuery = useMemo(
    () => ({
      sectionIds: sectionIds.length > 0 ? sectionIds : undefined
    }),
    [sectionIds]
  );

  const scheduleDateRange = useMemo(() => getDashboardScheduleDateRange(), []);
  const skip = !canViewEvents || !sectionIdsReady || sectionIds.length === 0;

  const {
    data: scheduleData,
    previousData: previousScheduleData,
    loading: scheduleLoading,
    networkStatus: scheduleNetworkStatus,
    error: scheduleError
  } = useQuery(DASHBOARD_EVENTS_QUERY, {
    skip,
    variables: {
      first: DASHBOARD_SCHEDULE_MAX_PAGE_SIZE,
      query: {
        status: [...SCHEDULE_STATUS_VALUES],
        dateRange: scheduleDateRange,
        ...sharedQuery
      },
      orderBy: {
        field: "STARTS_AT",
        direction: "ASC"
      }
    }
  });

  const {
    data: pastData,
    previousData: previousPastData,
    loading: pastLoading,
    networkStatus: pastNetworkStatus,
    error: pastError
  } = useQuery(DASHBOARD_EVENTS_QUERY, {
    skip,
    variables: {
      first: DASHBOARD_PAST_EVENT_LIMIT,
      query: {
        status: ["Completed"],
        ...sharedQuery
      },
      orderBy: {
        field: "ENDS_AT",
        direction: "DESC"
      }
    }
  });

  const resolvedScheduleData = scheduleData ?? previousScheduleData;
  const resolvedPastData = pastData ?? previousPastData;
  const scheduleEvents = resolvedScheduleData?.events.nodes ?? [];

  const scheduleByMonth = useMemo(
    () => groupScheduleEventsByMonth(resolvedScheduleData?.events.nodes ?? []),
    [resolvedScheduleData?.events.nodes]
  );

  const pastEvents = useMemo(
    () => (resolvedPastData?.events.nodes ?? []).map(toDashboardPastEvent),
    [resolvedPastData?.events.nodes]
  );

  const scheduleQueryLoading = isInitialQueryLoading({
    loading: scheduleLoading,
    networkStatus: scheduleNetworkStatus,
    itemCount: scheduleEvents.length,
    hasData: resolvedScheduleData !== undefined,
    error: scheduleError
  });

  const pastQueryLoading = isInitialQueryLoading({
    loading: pastLoading,
    networkStatus: pastNetworkStatus,
    itemCount: pastEvents.length,
    hasData: resolvedPastData !== undefined,
    error: pastError
  });

  return {
    canViewEvents,
    sectionScopeReady: sectionIdsReady,
    hasSectionScope: sectionIds.length > 0,
    scopedSectionNames,
    scheduleByMonth,
    scheduleEventCount: scheduleEvents.length,
    pastEvents,
    scheduleLoading: sectionIdsLoading || (!skip && scheduleQueryLoading),
    pastLoading: sectionIdsLoading || (!skip && pastQueryLoading),
    scheduleError,
    pastError
  };
}
